import { useEffect, useRef, type RefObject } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import { CAMERA_PRESETS, type CameraPresetId } from './cameraPresets';

// Re-export so TacticsBoard3D shares one ref type without importing
// `three-stdlib` directly (it's a transitive dep via @react-three/drei).
export type OrbitControlsHandle = OrbitControlsImpl;

interface Props {
  preset: CameraPresetId;
  controlsRef: RefObject<OrbitControlsHandle | null>;
  /** Tween length in ms. 0 = teleport (used by tests). */
  durationMs?: number;
}

interface Tween {
  startPos: THREE.Vector3;
  endPos: THREE.Vector3;
  startTarget: THREE.Vector3;
  endTarget: THREE.Vector3;
  startFov: number;
  endFov: number;
  t0: number;
  duration: number;
  active: boolean;
}

// Cubic ease-out — fast at the start, settles softly. Cinematic without overshoot.
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Drives the active R3F camera + OrbitControls target toward the named
 * preset whenever `preset` changes. The motion is tweened (default 700ms,
 * cubic ease-out) — per spec §16 cinematic feel matters here. After the
 * tween completes the user keeps full pan/zoom via OrbitControls.
 *
 * Re-selecting the same preset (via the snapNonce reset trick in
 * TacticsBoard3D) re-runs the tween, which is exactly what coaches expect
 * from "snap me back" buttons.
 */
export function CameraRig({ preset, controlsRef, durationMs = 700 }: Props) {
  const { camera } = useThree();
  const tweenRef = useRef<Tween | null>(null);

  // On preset change: capture current camera state and queue a tween toward
  // the preset's target. Reads from `camera` and `controlsRef.current.target`
  // so even mid-tween changes start cleanly from "wherever we are now".
  useEffect(() => {
    if (!camera) return;
    const config = CAMERA_PRESETS.find((p) => p.id === preset);
    if (!config) return;

    const startPos = camera.position.clone();
    const startTarget = controlsRef.current
      ? controlsRef.current.target.clone()
      : new THREE.Vector3(0, 0, 0);
    const startFov = isPerspective(camera) ? camera.fov : 32;

    if (durationMs === 0) {
      // Teleport — tests rely on this path; no useFrame loop runs.
      camera.position.set(...config.position);
      if (isPerspective(camera) && config.fov !== undefined) {
        camera.fov = config.fov;
        camera.updateProjectionMatrix();
      }
      if (controlsRef.current) {
        controlsRef.current.target.set(...config.target);
        controlsRef.current.update();
      } else {
        camera.lookAt(config.target[0], config.target[1], config.target[2]);
      }
      tweenRef.current = null;
      return;
    }

    tweenRef.current = {
      startPos,
      endPos: new THREE.Vector3(...config.position),
      startTarget,
      endTarget: new THREE.Vector3(...config.target),
      startFov,
      endFov: config.fov ?? startFov,
      t0: performance.now(),
      duration: durationMs,
      active: true,
    };
  }, [preset, camera, controlsRef, durationMs]);

  useFrame(() => {
    const tween = tweenRef.current;
    if (!tween || !tween.active) return;
    const elapsed = performance.now() - tween.t0;
    const raw = Math.min(elapsed / tween.duration, 1);
    const t = easeOutCubic(raw);

    camera.position.lerpVectors(tween.startPos, tween.endPos, t);

    if (isPerspective(camera) && tween.startFov !== tween.endFov) {
      camera.fov = tween.startFov + (tween.endFov - tween.startFov) * t;
      camera.updateProjectionMatrix();
    }

    if (controlsRef.current) {
      controlsRef.current.target.lerpVectors(tween.startTarget, tween.endTarget, t);
      controlsRef.current.update();
    } else {
      const lookAt = new THREE.Vector3();
      lookAt.lerpVectors(tween.startTarget, tween.endTarget, t);
      camera.lookAt(lookAt);
    }

    if (raw >= 1) tween.active = false;
  });

  return null;
}

function isPerspective(c: THREE.Camera): c is THREE.PerspectiveCamera {
  return (c as THREE.PerspectiveCamera).isPerspectiveCamera === true;
}
